import { useState } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Calendar, Tag, ImageIcon, Phone, Mail, Globe, Navigation, X, ChevronLeft, ChevronRight } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type SharedPhoto = {
  id: number;
  url: string;
  area: string | null;
  note: string | null;
  tags: string[];
  takenAt: string | null;
  createdAt: string;
  latitude: number | null;
  longitude: number | null;
};

type Branding = {
  companyName: string | null;
  logoBase64: string | null;
  accentColor: string;
  phone: string | null;
  email: string | null;
  website: string | null;
};

type ShareData = {
  project: {
    id: number;
    name: string;
    address: string | null;
    status: string;
    startDate: string | null;
    photoCount: number;
  };
  photos: SharedPhoto[];
  branding: Branding;
  generatedAt: string;
};

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Returns black or white depending on which has better contrast against bg */
function contrastColor(hex: string): string {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  // Perceived luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#111827" : "#ffffff";
}

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  completed: "Completed",
  on_hold: "On Hold",
  cancelled: "Canceled",
};

// ── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({
  photos,
  startIndex,
  accent,
  onClose,
}: {
  photos: SharedPhoto[];
  startIndex: number;
  accent: string;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(startIndex);
  const photo = photos[idx];

  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);

  const dateStr = photo.takenAt
    ? new Date(photo.takenAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : new Date(photo.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  const gpsUrl = photo.latitude != null && photo.longitude != null
    ? `https://maps.google.com/?q=${photo.latitude},${photo.longitude}`
    : null;

  // Close on backdrop click
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Arrow keys
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") prev();
    else if (e.key === "ArrowRight") next();
    else if (e.key === "Escape") onClose();
  };

  return (
    <div
      onClick={handleBackdrop}
      onKeyDown={handleKey}
      tabIndex={0}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.92)",
        display: "flex", flexDirection: "column",
        outline: "none",
      }}
    >
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.75rem 1rem", flexShrink: 0 }}>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8125rem" }}>
          {idx + 1} / {photos.length}
          {photo.area && <span style={{ marginLeft: "0.5rem", color: "rgba(255,255,255,0.45)" }}>· {photo.area}</span>}
        </span>
        <button
          onClick={onClose}
          style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: "2rem", height: "2rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}
        >
          <X style={{ width: "1rem", height: "1rem" }} />
        </button>
      </div>

      {/* Image + nav */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: 0, padding: "0 3.5rem" }}>
        <img
          src={photo.url}
          alt={photo.area ?? "Site photo"}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "0.375rem" }}
        />
        {photos.length > 1 && (
          <>
            <button
              onClick={prev}
              style={{ position: "absolute", left: "0.5rem", background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: "2.5rem", height: "2.5rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}
            >
              <ChevronLeft style={{ width: "1.25rem", height: "1.25rem" }} />
            </button>
            <button
              onClick={next}
              style={{ position: "absolute", right: "0.5rem", background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: "2.5rem", height: "2.5rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}
            >
              <ChevronRight style={{ width: "1.25rem", height: "1.25rem" }} />
            </button>
          </>
        )}
      </div>

      {/* Metadata strip */}
      <div style={{ padding: "0.875rem 1.25rem 1.25rem", flexShrink: 0, display: "flex", flexWrap: "wrap", gap: "0.625rem 1.25rem", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8125rem", color: "rgba(255,255,255,0.6)" }}>
          <Calendar style={{ width: "0.75rem", height: "0.75rem" }} />
          {dateStr}
        </div>
        {photo.note && (
          <p style={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.9)", margin: 0, width: "100%", lineHeight: 1.5 }}>{photo.note}</p>
        )}
        {photo.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
            {photo.tags.map((tag) => (
              <span key={tag} style={{ fontSize: "0.6875rem", background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.8)", padding: "0.15rem 0.5rem", borderRadius: "0.25rem", display: "flex", alignItems: "center", gap: "0.2rem" }}>
                <Tag style={{ width: "0.5rem", height: "0.5rem" }} />{tag}
              </span>
            ))}
          </div>
        )}
        {gpsUrl && (
          <a href={gpsUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.75rem", color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>
            <Navigation style={{ width: "0.625rem", height: "0.625rem" }} />
            {photo.latitude!.toFixed(5)}, {photo.longitude!.toFixed(5)}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function FieldSharePage() {
  const { token } = useParams<{ token: string }>();
  const [lightbox, setLightbox] = useState<{ photos: SharedPhoto[]; index: number } | null>(null);

  const shareQuery = useQuery<ShareData>({
    queryKey: ["field", "share", token],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/share/${token}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!token,
  });

  if (shareQuery.isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
        <Loader2 style={{ width: "2rem", height: "2rem", color: "#6b7280" }} className="animate-spin" />
      </div>
    );
  }

  if (shareQuery.isError || !shareQuery.data) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f9fafb", padding: "2rem", textAlign: "center" }}>
        <ImageIcon style={{ width: "3rem", height: "3rem", marginBottom: "1rem", color: "#d1d5db" }} />
        <h2 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.5rem", color: "#111827" }}>Link not found</h2>
        <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>This share link may have expired or been removed.</p>
      </div>
    );
  }

  const { project, photos, branding, generatedAt } = shareQuery.data;
  const accent = branding.accentColor ?? "#1e3a5f";
  const onAccent = contrastColor(accent);

  const areas = Array.from(new Set(photos.map((p) => p.area).filter(Boolean))) as string[];

  const logoSrc = branding.logoBase64
    ? (branding.logoBase64.startsWith("data:") ? branding.logoBase64 : `data:image/png;base64,${branding.logoBase64}`)
    : null;

  const openLightbox = (photoList: SharedPhoto[], index: number) => {
    setLightbox({ photos: photoList, index });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "system-ui, -apple-system, sans-serif" }}>

      {/* ── White header with company branding ──────────────────────────── */}
      <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "0 1.5rem" }}>
        <div style={{ maxWidth: "72rem", margin: "0 auto", padding: "1rem 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
            {logoSrc && (
              <img
                src={logoSrc}
                alt={branding.companyName ?? "Company logo"}
                style={{ height: "2.25rem", maxWidth: "7rem", objectFit: "contain" }}
              />
            )}
            {branding.companyName && (
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "#111827" }}>
                {branding.companyName}
              </span>
            )}
          </div>
          <span style={{ fontSize: "0.7rem", color: "#9ca3af", textAlign: "right", flexShrink: 0 }}>
            {new Date(generatedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
          </span>
        </div>
      </div>

      {/* ── Brand-colored project hero ───────────────────────────────────── */}
      <div style={{ background: accent, padding: "0 1.5rem" }}>
        <div style={{ maxWidth: "72rem", margin: "0 auto", padding: "1.75rem 0 1.5rem" }}>
          <h1 style={{ fontSize: "1.625rem", fontWeight: 800, color: onAccent, margin: "0 0 0.375rem" }}>
            {project.name}
          </h1>
          {project.address && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.875rem", color: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "111827", 0.72), marginBottom: "1rem" }}>
              <MapPin style={{ width: "0.875rem", height: "0.875rem", flexShrink: 0 }} />
              {project.address}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.875rem", alignItems: "center" }}>
            <Stat value={photos.length} label={`Photo${photos.length !== 1 ? "s" : ""}`} accent={accent} onAccent={onAccent} />
            {areas.length > 0 && (
              <Stat value={areas.length} label={`Area${areas.length !== 1 ? "s" : ""}`} accent={accent} onAccent={onAccent} />
            )}
            <div style={{
              padding: "0.25rem 0.875rem",
              borderRadius: "9999px",
              fontSize: "0.75rem",
              fontWeight: 700,
              background: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "000000", 0.15),
              color: onAccent,
              border: `1px solid ${hexToRgba(onAccent === "#ffffff" ? "ffffff" : "000000", 0.2)}`,
              letterSpacing: "0.02em",
            }}>
              {STATUS_LABELS[project.status] ?? project.status}
            </div>
          </div>
        </div>
      </div>

      {/* ── Photo sections ───────────────────────────────────────────────── */}
      <div style={{ maxWidth: "72rem", margin: "0 auto", padding: "2rem 1.25rem" }}>
        {photos.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4rem 2rem", textAlign: "center", color: "#9ca3af" }}>
            <ImageIcon style={{ width: "3rem", height: "3rem", marginBottom: "0.75rem", opacity: 0.4 }} />
            <p style={{ fontWeight: 500, color: "#6b7280" }}>No photos in this report</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
            {areas.length === 0 ? (
              <PhotoSection photos={photos} accent={accent} onOpen={openLightbox} />
            ) : (
              <>
                {photos.some((p) => !p.area) && (
                  <section>
                    <AreaHeading label="Uncategorized" count={photos.filter((p) => !p.area).length} accent={accent} />
                    <PhotoSection photos={photos.filter((p) => !p.area)} accent={accent} onOpen={openLightbox} />
                  </section>
                )}
                {areas.map((area) => {
                  const areaPhotos = photos.filter((p) => p.area === area);
                  return (
                    <section key={area}>
                      <AreaHeading label={area} count={areaPhotos.length} accent={accent} />
                      <PhotoSection photos={areaPhotos} accent={accent} onOpen={openLightbox} />
                    </section>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      {(branding.companyName || branding.phone || branding.email || branding.website) && (
        <div style={{ background: accent, marginTop: "1rem" }}>
          <div style={{ maxWidth: "72rem", margin: "0 auto", padding: "1.25rem 1.5rem", display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ color: onAccent, fontWeight: 600, fontSize: "0.9375rem" }}>
              {branding.companyName}
            </span>
            <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
              {branding.phone && (
                <a href={`tel:${branding.phone}`} style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "111827", 0.8), fontSize: "0.8125rem", textDecoration: "none" }}>
                  <Phone style={{ width: "0.875rem", height: "0.875rem" }} />{branding.phone}
                </a>
              )}
              {branding.email && (
                <a href={`mailto:${branding.email}`} style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "111827", 0.8), fontSize: "0.8125rem", textDecoration: "none" }}>
                  <Mail style={{ width: "0.875rem", height: "0.875rem" }} />{branding.email}
                </a>
              )}
              {branding.website && (
                <a href={branding.website.startsWith("http") ? branding.website : `https://${branding.website}`} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "111827", 0.8), fontSize: "0.8125rem", textDecoration: "none" }}>
                  <Globe style={{ width: "0.875rem", height: "0.875rem" }} />{branding.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox overlay ─────────────────────────────────────────────── */}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          accent={accent}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

// ── Stat chip on hero ────────────────────────────────────────────────────────
function Stat({ value, label, accent, onAccent }: { value: number; label: string; accent: string; onAccent: string }) {
  return (
    <div style={{
      background: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "000000", 0.12),
      borderRadius: "0.5rem",
      padding: "0.375rem 0.875rem",
      display: "flex", alignItems: "baseline", gap: "0.375rem",
    }}>
      <span style={{ fontSize: "1.25rem", fontWeight: 800, color: onAccent, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: "0.75rem", color: hexToRgba(onAccent === "#ffffff" ? "ffffff" : "111827", 0.72) }}>{label}</span>
    </div>
  );
}

// ── Area heading ─────────────────────────────────────────────────────────────
function AreaHeading({ label, count, accent }: { label: string; count: number; accent: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", marginBottom: "0.875rem" }}>
      <MapPin style={{ width: "1rem", height: "1rem", color: accent, flexShrink: 0 }} />
      <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "#111827", margin: 0 }}>{label}</h2>
      <span style={{ fontSize: "0.8rem", color: "#9ca3af" }}>({count})</span>
      <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
    </div>
  );
}

// ── Photo section — desktop cards, mobile gallery ────────────────────────────
function PhotoSection({
  photos,
  accent,
  onOpen,
}: {
  photos: SharedPhoto[];
  accent: string;
  onOpen: (photos: SharedPhoto[], index: number) => void;
}) {
  return (
    <>
      {/* Desktop: metadata cards */}
      <div className="hidden-on-mobile" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "1rem" }}>
        {photos.map((photo, i) => (
          <DesktopPhotoCard key={photo.id} photo={photo} accent={accent} onClick={() => onOpen(photos, i)} />
        ))}
      </div>

      {/* Mobile: tight 2-col gallery, tap to expand */}
      <div className="mobile-only" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.375rem" }}>
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            onClick={() => onOpen(photos, i)}
            style={{ position: "relative", paddingTop: "100%", background: "#111827", borderRadius: "0.375rem", overflow: "hidden", border: "none", cursor: "pointer", display: "block", width: "100%" }}
          >
            <img
              src={photo.url}
              alt={photo.area ?? "Site photo"}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              loading="lazy"
            />
            {/* Subtle gradient + area label at bottom */}
            {photo.area && (
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(0,0,0,0.55))", padding: "1rem 0.375rem 0.375rem", display: "flex", alignItems: "flex-end" }}>
                <span style={{ fontSize: "0.625rem", fontWeight: 600, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{photo.area}</span>
              </div>
            )}
            {/* Note indicator dot */}
            {photo.note && (
              <div style={{ position: "absolute", top: "0.375rem", right: "0.375rem", width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: accent, boxShadow: "0 0 0 1.5px white" }} />
            )}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Desktop photo card ───────────────────────────────────────────────────────
function DesktopPhotoCard({ photo, accent, onClick }: { photo: SharedPhoto; accent: string; onClick: () => void }) {
  const dateStr = photo.takenAt
    ? new Date(photo.takenAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : new Date(photo.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  const gpsUrl = photo.latitude != null && photo.longitude != null
    ? `https://maps.google.com/?q=${photo.latitude},${photo.longitude}`
    : null;

  return (
    <div
      style={{ borderRadius: "0.625rem", overflow: "hidden", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e5e7eb", cursor: "pointer" }}
      onClick={onClick}
    >
      <div style={{ position: "relative", paddingTop: "70%", background: "#111827" }}>
        <img
          src={photo.url}
          alt={photo.area ?? "Site photo"}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          loading="lazy"
        />
        {photo.area && (
          <div style={{ position: "absolute", bottom: "0.5rem", left: "0.5rem" }}>
            <span style={{ background: "rgba(0,0,0,0.65)", color: "white", fontSize: "0.6875rem", fontWeight: 600, padding: "0.15rem 0.5rem", borderRadius: "0.25rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <MapPin style={{ width: "0.625rem", height: "0.625rem" }} />{photo.area}
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: "0.625rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.375rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.75rem", color: "#6b7280" }}>
          <Calendar style={{ width: "0.625rem", height: "0.625rem", flexShrink: 0 }} />{dateStr}
        </div>
        {photo.note && (
          <p style={{ fontSize: "0.8125rem", color: "#374151", lineHeight: 1.45, margin: 0 }}>{photo.note}</p>
        )}
        {photo.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
            {photo.tags.map((tag) => (
              <span key={tag} style={{ fontSize: "0.65rem", background: "#f3f4f6", color: "#4b5563", padding: "0.1rem 0.45rem", borderRadius: "0.25rem", display: "flex", alignItems: "center", gap: "0.15rem", border: "1px solid #e5e7eb" }}>
                <Tag style={{ width: "0.5rem", height: "0.5rem" }} />{tag}
              </span>
            ))}
          </div>
        )}
        {gpsUrl && (
          <a href={gpsUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.6875rem", color: accent, textDecoration: "none", marginTop: "0.1rem" }}>
            <Navigation style={{ width: "0.625rem", height: "0.625rem" }} />
            {photo.latitude!.toFixed(5)}, {photo.longitude!.toFixed(5)}
          </a>
        )}
      </div>
    </div>
  );
}
